/**
 * Canonical user-role enum for the Arterra frontend.
 *
 * - `admin`       — Arterra platform staff. Assigned manually; never self-select.
 * - `investor`    — External user managing their own deal pipeline.
 * - `developer`   — External user uploading their own infrastructure projects.
 * - `raise_owner` — Scoped persona for a claimed-raise owner. Can reach ONLY the
 *                   owner console (`/owner`) and their public raise profile; walled
 *                   off from the rest of the platform at both the shell and the API.
 *                   Stamped at claim verification (`approved:false`, no paid sub) and
 *                   NEVER routed through admin approval (`approved:true` is a master
 *                   key). Assigned manually at provisioning; never self-select.
 *
 * `admin` and `raise_owner` are intentionally excluded from public registration.
 * The frontend registration UI exposes only `PUBLIC_USER_ROLES`.
 *
 * On the user object, role lives in two places that always match:
 *   1. `users/{uid}.role` in Firestore (canonical persisted field)
 *   2. Firebase Auth customClaims.role (mirrored for JWT-fast access)
 *
 * The legacy duplicate `userType` field is being removed; do not introduce
 * new references to it.
 */

export const USER_ROLES = ['admin', 'investor', 'developer', 'raise_owner'] as const;
export type UserRole = (typeof USER_ROLES)[number];

/**
 * Roles a user may select when registering through the public signup flow.
 * `admin` and `raise_owner` are intentionally absent — both are provisioned
 * server-side only.
 */
export const PUBLIC_USER_ROLES = ['investor', 'developer'] as const;
export type PublicUserRole = (typeof PUBLIC_USER_ROLES)[number];

export function isUserRole(value: unknown): value is UserRole {
  return typeof value === 'string' && (USER_ROLES as readonly string[]).includes(value);
}

export function isPublicUserRole(value: unknown): value is PublicUserRole {
  return typeof value === 'string' && (PUBLIC_USER_ROLES as readonly string[]).includes(value);
}
